import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import * as dayjs from 'dayjs';
import isNil from 'lodash-es/isNil';
import orderBy from 'lodash-es/orderBy';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { Observable, combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { AppState } from '../app.models';
import * as fromRouter from '../router';
import { DeleteDialogComponent } from '../shared/components/delete-dialog/delete-dialog.component';
import { DeleteDlgData, FilterGroup, FilterType, MenuAction } from '../shared/shared.models';
import * as fromUsers from '../users';
import { checkPermissions } from '../users/user.utils';
import { DialogWidths } from '../utils';
import * as fromFirmwares from './';
import { AddFirmwaresDialogComponent } from './dialogs/add-firmwares-dialog/add-firmwares-dialog.component';
import { CreateFirmwareDto } from './firmwares.models';

enum FirmwareMenuAction {
    DELETE = 'DELETE',
    RELEASE = 'RELEASE',
}

@Component({
    selector: 'liebherr-firmwares',
    templateUrl: 'firmwares.component.html',
    styleUrls: ['firmwares.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmwaresComponent extends SubscriptionComponent implements AfterViewInit {
    getFirmwarePermissions = fromFirmwares.getFirmwarePermissions;
    loggedInUser$: Observable<fromUsers.LoggedInUser> = this.store.select(fromUsers.selectLoggedInUser);

    filterParams$: Observable<fromRouter.FilterParams> = this.store.select(fromRouter.selectFilterParams);
    loading$ = this.store.select(fromFirmwares.selectLoading);
    firmwares$: Observable<fromFirmwares.Firmware[]>;
    filterGroups: FilterGroup[] = this.getFilterGroups();

    actionMenuItems: MenuAction[] = [];

    displayedColumns = [
        fromFirmwares.FirmwareColumns.ACTIVE,
        fromFirmwares.FirmwareColumns.VERSION,
        fromFirmwares.FirmwareColumns.ROUTERTYPE,
        fromFirmwares.FirmwareColumns.VENDOR,
        fromFirmwares.FirmwareColumns.RELEASED_AT,
        fromFirmwares.FirmwareColumns.RELEASED_BY,
        fromFirmwares.FirmwareColumns.HAS_FILE,
        fromFirmwares.FirmwareColumns.ACTIONS,
    ];
    ACTION = FirmwareMenuAction;
    COLUMNS = fromFirmwares.FirmwareColumns;

    dataSource = new MatTableDataSource<fromFirmwares.Firmware>([]);

    @ViewChild(MatPaginator) paginator?: MatPaginator;

    constructor(private store: Store<AppState>, public dialog: MatDialog, public routerService: fromRouter.RouterService) {
        super();

        this.store.dispatch(fromFirmwares.loadFirmwares());

        this.firmwares$ = combineLatest([this.store.select(fromFirmwares.selectFirmwares), this.filterParams$]).pipe(
            map(([fw, filterParams]) => {
                const firmwares: fromFirmwares.Firmware[] = fw;
                // order and filter the firmwares by set query params
                return orderBy(
                    firmwares.filter((fw) => {
                        const versionMatch = !isNil(filterParams.fwVersion)
                            ? fw.version?.toLowerCase().includes(filterParams.fwVersion?.toLowerCase())
                            : true;
                        const releasedAt = !isNil(fw.releasedAt) ? dayjs(fw.releasedAt) : undefined;
                        const releasedAtMatch = !isNil(filterParams.fwReleasedAt)
                            ? releasedAt?.isSame(filterParams.fwReleasedAt, 'day')
                            : true;
                        const releasedByMatch = !isNil(filterParams.fwReleasedBy)
                            ? fw.releasedBy?.username?.toLowerCase().includes(filterParams.fwReleasedBy?.toLowerCase())
                            : true;

                        return versionMatch && releasedAtMatch && releasedByMatch;
                    }),
                    (fw) => {
                        // get poperty to sort by
                        switch (filterParams.sortColumn) {
                            case fromFirmwares.FirmwareColumns.ACTIVE: {
                                return fw.activeOn;
                            }
                            case fromFirmwares.FirmwareColumns.VERSION: {
                                return fw.version;
                            }
                            case fromFirmwares.FirmwareColumns.ROUTERTYPE: {
                                return fw.routerType;
                            }
                            case fromFirmwares.FirmwareColumns.VENDOR: {
                                return fw.vendor;
                            }
                            case fromFirmwares.FirmwareColumns.RELEASED_AT: {
                                return fw.releasedAt;
                            }
                            case fromFirmwares.FirmwareColumns.RELEASED_BY: {
                                return fw.releasedBy;
                            }
                            default: {
                                return fw.version;
                            }
                        }
                    },
                    filterParams.sortAsc === true ? 'asc' : 'desc'
                );
            })
        );

        // PERMISSION CHECKS DIRECT-ACTIONS
        this.sub.add(
            this.loggedInUser$.pipe(first((lu) => lu !== undefined)).subscribe((loggedInUser) => {
                Object.keys(FirmwareMenuAction).forEach((action) => {
                    if (
                        action === FirmwareMenuAction.DELETE &&
                        checkPermissions(loggedInUser, fromFirmwares.getFirmwarePermissions('deleteFirmware'))
                    ) {
                        this.actionMenuItems.push({
                            label: 'FIRMWARES.OPTIONS.' + action,
                            event: FirmwareMenuAction[action],
                        });
                    }
                    if (
                        action === FirmwareMenuAction.RELEASE &&
                        checkPermissions(loggedInUser, fromFirmwares.getFirmwarePermissions('releaseFirmware'))
                    ) {
                        this.actionMenuItems.push({
                            label: 'FIRMWARES.OPTIONS.' + action,
                            event: FirmwareMenuAction[action],
                        });
                    }
                });
            })
        );
    }
    ngAfterViewInit(): void {
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }

        this.sub.add(
            this.firmwares$.subscribe((firmwares) => {
                this.dataSource.data = firmwares;
            })
        );
    }

    onAddFirmware() {
        const dialogRef = this.dialog.open<AddFirmwaresDialogComponent, any>(AddFirmwaresDialogComponent, {
            data: {},
            disableClose: true,
            width: DialogWidths.SMALL,
        });
        dialogRef.afterClosed().subscribe((data?: CreateFirmwareDto) => {
            if (data) {
                this.store.dispatch(fromFirmwares.addFirmware({ firmware: data }));
            }
        });
    }

    onActionMenuClick(event: string, element: fromFirmwares.Firmware) {
        switch (event) {
            case FirmwareMenuAction.DELETE: {
                if (element.id) {
                    this.onDelete(element);
                }
                break;
            }
            case FirmwareMenuAction.RELEASE: {
                if (element.id) {
                    this.onRelease(element);
                }
                break;
            }

            default: {
                break;
            }
        }
    }

    private getFilterGroups(): FilterGroup[] {
        return [
            {
                controls: [
                    {
                        type: FilterType.SEARCH,
                        label: 'FIRMWARES.COLUMNS.VERSION',
                        paramsKey: fromRouter.ParamsKey.FW_VERSION,
                    },
                    {
                        type: FilterType.DATE_PICKER,
                        label: 'FIRMWARES.COLUMNS.RELEASED_AT',
                        paramsKey: fromRouter.ParamsKey.FW_RELEASED_AT,
                    },
                    {
                        type: FilterType.SEARCH,
                        label: 'FIRMWARES.COLUMNS.RELEASED_BY',
                        paramsKey: fromRouter.ParamsKey.FW_RELEASED_BY,
                    },
                ],
            },
        ];
    }

    private onDelete(fw: fromFirmwares.Firmware) {
        if (fw.id) {
            const dialogRef = this.dialog.open<DeleteDialogComponent, DeleteDlgData>(DeleteDialogComponent, {
                data: {
                    title: translate('FIRMWARES.DELETE_TITLE'),
                    hint: translate('FIRMWARES.DELETE_HINT', { version: fw.version }),
                },
                disableClose: true,
                width: DialogWidths.SMALL,
            });
            dialogRef.afterClosed().subscribe((data: boolean) => {
                if (data === true) {
                    this.store.dispatch(fromFirmwares.deleteFirmware({ id: fw.id! }));
                }
            });
        }
    }
    private onRelease(fw: fromFirmwares.Firmware) {
        if (fw.id) {
            this.store.dispatch(fromFirmwares.releaseFirmware({ id: fw.id }));
        }
    }
}
